export interface UserProfile {
  upn: string;
  role: string;
  claimsAccess?: { code: string }[];
}


/**
 * Permission codes specific to the TicketEasy module
 */
export enum TicketPermission {
  // Administrative permissions
  TicketAdmin = "TicketAdmin",
  GlobalAdmin = "Admin",
  
  // Specific feature permissions
  TicketCreator = "TicketCreator",
  TicketApprover = "TicketApprover",
  TicketViewer = "TicketViewer",
  
  // Special permissions
  IncidentAdmin = "IncidentAdmin",
  TestTeam = "TestTeam"
}

/**
 * Role types that may have specific permissions
 */
export enum UserRole {
  Admin = "Admin",
  TestTeam = "Test Team",
  Manager = "Manager",
  User = "User"
}

/**
 * Check if a user has admin access to the Ticket module
 * @param userProfile - The user profile object
 * @returns boolean indicating whether the user has admin access
 */
export const hasAdminAccess = (userProfile?: UserProfile | null): boolean => {
  if (!userProfile) return false;
  
  // Admin role has full access
  if (userProfile.role === UserRole.Admin) return true;
  
  // Check for specific admin permissions
  if (userProfile.claimsAccess && 
      userProfile.claimsAccess.find(p => 
        p.code === TicketPermission.TicketAdmin || 
        p.code === TicketPermission.GlobalAdmin
      ) !== undefined) {
    return true;
  }
  
  return false;
};

/**
 * Check if user has a specific permission
 * @param userProfile - The user profile object
 * @param permission - The permission code to check for
 * @returns boolean indicating whether the user has the specified permission
 */
export const hasPermission = (
  userProfile?: UserProfile | null,
  permission?: TicketPermission | string
): boolean => {
  if (!userProfile || !permission) return false;
  
  // Admin role has all permissions
  if (userProfile.role === UserRole.Admin) return true;
  
  // Check for the specific permission in claims
  if (userProfile.claimsAccess && 
      userProfile.claimsAccess.find(p => p.code === permission) !== undefined) {
    return true;
  }
  
  // Special case for Test Team role
  if (permission === TicketPermission.TestTeam && 
      userProfile.role === UserRole.TestTeam) {
    return true;
  }
  
  return false;
};

/**
 * Check if user can edit a ticket
 * @param userProfile - The user profile object
 * @param ticket - The ticket object
 * @returns boolean indicating whether the user can edit the ticket
 */
export const canEditTicket = (
  userProfile?: UserProfile | null,
  ticket?: any // Replace with your actual ticket type
): boolean => {
  if (!userProfile || !ticket) return false;
  
  // Check if ticket is in a status that allows editing
  if (ticket.status === "Complete") return false;
  
  // Admins can edit all tickets
  if (hasAdminAccess(userProfile)) return true;
  
  // Test Team can edit tickets
  if (userProfile.role === UserRole.TestTeam) return true;
  if (hasPermission(userProfile, TicketPermission.TestTeam)) return true;
  
  // Check if user is assigned to the ticket
  if (ticket.assignedUsers && 
      ticket.assignedUsers.find((p: any) => p.upn === userProfile.upn)) {
    return true;
  }
  
  return false;
};

/**
 * Check if a user has viewer permission
 * @param userProfile - The user profile object
 * @returns boolean indicating whether the user can view tickets
 */
export const hasViewAccess = (userProfile?: UserProfile | null): boolean => {
  if (!userProfile) return false;
  
  // All of these roles/permissions can view tickets
  if (hasAdminAccess(userProfile)) return true;
  if (hasPermission(userProfile, TicketPermission.TicketViewer)) return true;
  if (hasPermission(userProfile, TicketPermission.TicketCreator)) return true;
  if (hasPermission(userProfile, TicketPermission.TicketApprover)) return true;
  
  return false;
};
